import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";
import { Card, Button, Tag, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SignatureOutlined,
  FolderViewOutlined,
  SyncOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import styles from "./ServiceCollectionListItem.module.css";
import RequestAccessToServiceCollectionButton from "../subscriptionActions/RequestAccessToServiceCollectionButton";
import { getServiceCollectionEligibilitySettings } from "../../actions/api";
import { useDispatch } from "react-redux";
import SubscribeToApplicationButton from "../subscriptionActions/SubscribeToApplicationButton";

const { Meta } = Card;

const ServiceCollectionListItem = ({ serviceCollection }) => {
  const dispatch = useDispatch();
  const [eligibilitySettings, setEligibilitySettings] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setEligibilitySettings({});
    const tmpSettings = await dispatch(
      getServiceCollectionEligibilitySettings(
        serviceCollection.serviceCollection.applicationIdentifier,
      ),
    );
    if (tmpSettings?.payload?.data)
      setEligibilitySettings(tmpSettings.payload.data);

    setIsLoading(false);
  }, [dispatch, serviceCollection]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleRefresh = () => {
    // dispatch(getMyRequests());
    fetchSettings();
  };

  const renderEligibilityTag = (serviceCollection, loading = false) => {
    if (loading) {
      return (
        <Tag icon={<SyncOutlined spin />} color="default">
          loading
        </Tag>
      );
    }
    switch (serviceCollection.status) {
      case "EligibleWithApproval":
        return (
          <Tag icon={<ExclamationCircleOutlined />} color="warning">
            Eligible with guarantor
          </Tag>
        );
      case "Eligible":
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Eligible
          </Tag>
        );
      case "Subscribed":
        return (
          <>
            <Tag icon={<CheckCircleOutlined />} color="success">
              Eligible
            </Tag>
            <Tag icon={<SignatureOutlined />} color="success">
              Subscribed
            </Tag>
          </>
        );
      case "NotEligible":
      default:
        return (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Not Eligible
          </Tag>
        );
    }
  };

  const renderEligibilityActions = (serviceCollection, eligibilitySettings) => {
    return [
      <RequestAccessToServiceCollectionButton
        key="zz1"
        serviceCollection={serviceCollection}
        eligibilitySettings={eligibilitySettings}
        disabled={serviceCollection.status !== "EligibleWithApproval"}
      />,
      serviceCollection.status === "Eligible" ? (
        eligibilitySettings?.selfSubscriptionGroupId &&
        eligibilitySettings?.subscribedRoleId ? (
          <SubscribeToApplicationButton
            key="zz2"
            subscribedRole={eligibilitySettings.subscribedRoleId}
            application={serviceCollection.serviceCollection}
            groupForSubscription={eligibilitySettings.selfSubscriptionGroupId}
            onChange={handleRefresh}
          />
        ) : (
          <Tooltip key="zz3" title="Self Subscription is not available">
            <Button type="text" disabled icon={<SignatureOutlined />}>
              Subscribe
            </Button>
          </Tooltip>
        )
      ) : (
        <Button
          key="zz4"
          type="text"
          disabled
          icon={<SignatureOutlined />}
          size="middle"
        >
          Subscribe
        </Button>
      ),
      <NavLink
        key="zz5"
        to={`/service/${serviceCollection.serviceCollection.applicationIdentifier}`}
      >
        <Button
          type="text"
          disabled={serviceCollection.status !== "Subscribed"}
          icon={<FolderViewOutlined />}
        >
          Open
        </Button>
      </NavLink>,
    ];
  };
  return (
    <Card
      className={styles.Card}
      classNames={{
        header: styles.CardHeader,
        actions: styles.CardActions,
      }}
      title={
        <>
          {serviceCollection.serviceCollection.displayName}
          {serviceCollection.serviceCollection.homePage ? (
            <Tooltip title={serviceCollection.serviceCollection.homePage}>
              <Button
                color="default"
                variant="text"
                style={{ color: "white" }}
                href={serviceCollection.serviceCollection.homePage}
                target="_blank"
                icon={<QuestionCircleOutlined />}
              />
            </Tooltip>
          ) : (
            <></>
          )}
        </>
      }
      actions={renderEligibilityActions(serviceCollection, eligibilitySettings)}
      loading={isLoading}
    >
      <Meta
        description={renderEligibilityTag(serviceCollection, isLoading)}
        className={styles.CardTags}
      />
      {serviceCollection.serviceCollection.description}
    </Card>
  );
};

ServiceCollectionListItem.propTypes = {
  serviceCollection: PropTypes.object.isRequired,
};

export default ServiceCollectionListItem;
